import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { TrendingUp, BarChart2 } from "lucide-react";

const COLORS = {
  confirmed: "#10b981",   // emerald
  cancelled: "#ef4444",   // red
  pending: "#f59e0b",     // amber
  in_progress: "#3b82f6", // blue
  completed: "#111827",   // near-black
};

export default function AdminBookingAnalytics() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/bookings/analytics?weeks=${weeks}`);
        if (alive) setData(res.data);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [weeks]);

  const svcName = (key) => t.services?.[key]?.name || t.specialty?.[key]?.name || key;

  const serviceRows = (data?.by_service_status || []).map(r => ({
    ...r,
    label: svcName(r.service),
  }));

  return (
    <div className="space-y-8" data-testid="admin-booking-analytics">
      {/* Header + range selector */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-1 flex items-center gap-2">
            <TrendingUp size={14} /> {t.admin_analytics.eyebrow}
          </p>
          <h2 className="text-2xl font-black tracking-tight">{t.admin_analytics.title}</h2>
          <p className="text-sm text-neutral-500 mt-1">{t.admin_analytics.subtitle}</p>
        </div>
        <div className="flex items-center gap-1 border border-neutral-200 bg-white p-1">
          {[4, 8, 13, 26].map(w => (
            <button
              key={w}
              type="button"
              onClick={() => setWeeks(w)}
              data-testid={`analytics-range-${w}`}
              className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 transition-colors ${
                weeks === w ? "bg-[#090A0B] text-white" : "text-neutral-500 hover:bg-neutral-100"
              }`}
            >
              {w}w
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 py-12 text-center">{t.common.loading}</div>
      ) : !data ? (
        <div className="text-neutral-400 py-12 text-center">—</div>
      ) : (
        <>
          {/* Weekly stacked line */}
          <div className="bg-white border border-neutral-200 p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-4 flex items-center gap-2">
              <BarChart2 size={14} /> {t.admin_analytics.weekly_title}
            </div>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={data.weekly} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="week_label" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#0A0B0D", border: "none", color: "#fff", fontSize: 12 }}
                    labelStyle={{ color: "#FF5A00", fontWeight: 700 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }} />
                  <Line type="monotone" dataKey="confirmed" name={t.dashboard.status.confirmed} stroke={COLORS.confirmed} strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cancelled" name={t.dashboard.status.cancelled} stroke={COLORS.cancelled} strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="completed" name={t.dashboard.status.completed} stroke={COLORS.completed} strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="pending" name={t.dashboard.status.pending} stroke={COLORS.pending} strokeWidth={2} dot={{ r: 2 }} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Service × status bars */}
          <div className="bg-white border border-neutral-200 p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-4">
              {t.admin_analytics.by_service_title}
            </div>
            {serviceRows.length === 0 ? (
              <div className="text-neutral-400 py-8 text-center">—</div>
            ) : (
              <div style={{ width: "100%", height: Math.max(220, serviceRows.length * 46) }}>
                <ResponsiveContainer>
                  <BarChart data={serviceRows} layout="vertical" margin={{ top: 8, right: 24, left: 24, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" stroke="#374151" tick={{ fontSize: 11 }} width={180} />
                    <Tooltip
                      contentStyle={{ background: "#0A0B0D", border: "none", color: "#fff", fontSize: 12 }}
                      labelStyle={{ color: "#FF5A00", fontWeight: 700 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }} />
                    <Bar dataKey="confirmed" name={t.dashboard.status.confirmed} stackId="s" fill={COLORS.confirmed} />
                    <Bar dataKey="completed" name={t.dashboard.status.completed} stackId="s" fill={COLORS.completed} />
                    <Bar dataKey="in_progress" name={t.dashboard.status.in_progress} stackId="s" fill={COLORS.in_progress} />
                    <Bar dataKey="pending" name={t.dashboard.status.pending} stackId="s" fill={COLORS.pending} />
                    <Bar dataKey="cancelled" name={t.dashboard.status.cancelled} stackId="s" fill={COLORS.cancelled} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {["confirmed", "completed", "in_progress", "pending", "cancelled"].map(s => {
              const total = (data.by_service_status || []).reduce((acc, r) => acc + (r[s] || 0), 0);
              return (
                <div key={s} className="bg-white border border-neutral-200 p-4" data-testid={`analytics-summary-${s}`}>
                  <div className="w-2 h-2" style={{ background: COLORS[s] }} />
                  <div className="text-2xl font-black tracking-tight mt-2">{total}</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mt-1">
                    {t.dashboard.status[s]}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
